import { Component, OnInit } from "@angular/core";
import { Action, Store } from "@ngrx/store";
import { NGXLogger } from "ngx-logger";
import { merge, Observable, Subscription } from "rxjs";
import { filter, map, withLatestFrom } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppRoutes } from "./app-routing.module";
import { getContextKey } from "./helpers/context-key.helper";
import { AuthService } from "./modules/auth/auth.service";
import { B2cService } from "./modules/auth/b2c.service";
import {
  selectDeltaModeState,
  selectWorkingCategoryState,
} from "./modules/category-builder/state/working-category.selectors";
import { ContextService } from "./modules/context/state/context.service";
import { selectFocus } from "./modules/focus/focus.selectors";
import { HomingPigeonService } from "./modules/homing-pigeon/homing-pigeon.service";
import { LocalStorageService } from "./modules/local-storage/local-storage.service";
import { LoggerConfigurationService } from "./modules/logging/logger-configuration.service";
import { SetLoggingState } from "./modules/logging/state/logging-state/logging.actions";
import { loggingFeatureKey } from "./modules/logging/state/logging-state/logging.reducer";
import { PadlockService } from "./modules/padlock/padlock.service";
import { GetPadlockTokenSuccess } from "./modules/padlock/state/padlock-token.actions";
import {
  padlockTokenFeatureKey,
  PadlockTokenState,
} from "./modules/padlock/state/padlock-token.reducer";
import { selectPadlockTokenState } from "./modules/padlock/state/padlock-token.selectors";
import { markAllPersistentNotificationViewed } from "./modules/persistent-notification/state/persistent-notification.actions";
import {
  PersistentNotification,
  PersistentNotificationType,
} from "./modules/persistent-notification/state/persistent-notification.model";
import { selectPersistentNotificationState } from "./modules/persistent-notification/state/persistent-notification.selectors";
import { KeybindService } from "./modules/shared/keybinding/keybind.service";
import { SetNavState } from "./modules/side-nav/state/side-nav/side-nav.actions";
import { sideNavFeatureKey } from "./modules/side-nav/state/side-nav/side-nav.reducer";
import {
  ThemeModelType,
  ThemeService,
  ThemeTypes,
} from "./modules/theme/theme.service";
import { UndoRedoService } from "./modules/undo-redo/undo-redo.service";
import { UserRoutes } from "./modules/user/user-routing.module";
import { SetActiveWorkspace } from "./modules/workspace/state/workspaces.actions";
import {
  partitionWorkspacesByOwnership,
  Workspace,
} from "./modules/workspace/state/workspaces.model";
import { WorkspacesState } from "./modules/workspace/state/workspaces.reducer";
import {
  selectActiveWorkspace,
  selectWorkspacesState,
} from "./modules/workspace/state/workspaces.selectors";
import { WorkspaceRoutes } from "./modules/workspace/workspace.routes";
import { WorkspaceService } from "./modules/workspace/workspace.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false,
})
export class AppComponent implements OnInit {
  APP_ROUTES = AppRoutes;
  USER_ROUTES = UserRoutes;
  WORKSPACE_ROUTES = WorkspaceRoutes;
  PersistentNotificationType = PersistentNotificationType;

  // Notifications
  notifications$ = this.store.select(selectPersistentNotificationState);
  notifications: PersistentNotification[];
  unviewedNotifications: number = 0;

  workspacesState$: Observable<WorkspacesState> = this.store.select(
    selectWorkspacesState,
  );
  workspace_: Subscription;
  workspace: Workspace;
  myWorkspaces: Workspace[] = [];
  otherWorkspaces: Workspace[] = [];
  isPlatformAdmin: boolean = false;
  private groupedWorkspaces: Workspace[] = [];
  isDarkMode: boolean = false;
  padlockTokenState$: Observable<PadlockTokenState> = this.store.select(
    selectPadlockTokenState,
  );
  decodedToken;

  constructor(
    private store: Store<any>,
    private loggingConfigService: LoggerConfigurationService,
    private localStorageService: LocalStorageService,
    private themeService: ThemeService,
    private logger: NGXLogger,
    private auth: AuthService,
    private workspaceService: WorkspaceService,
    // homingPigeonService appears unused but needs to be here for websockets to hook up correctly
    private homingPigeonService: HomingPigeonService,
    private padlock: PadlockService,
    private b2cService: B2cService,
    private keybinding: KeybindService,
    private undoRedo: UndoRedoService,
    private contextService: ContextService,
  ) {}

  ngOnInit() {
    this.startupMessage();
    this.initLog();
    this.initPadlock();
    this.initNav();
    this.b2cService.initMsal();

    this.padlockTokenState$.subscribe(() => {
      this.decodedToken = this.padlock.getDecodedToken();
      this.isPlatformAdmin = this.decodedToken?.platform_admin === true;
      this.groupWorkspaces();
    });

    this.workspacesState$.subscribe((state) => {
      this.groupedWorkspaces = state?.workspaces || [];
      this.groupWorkspaces();
    });

    this.workspace_ = this.store
      .select(selectActiveWorkspace)
      .subscribe((w) => this.onWorkspaceStateChange(w));

    this.themeService
      .watchTheme("selected-theme")
      .subscribe((value: ThemeModelType) => {
        if (value?.theme === ThemeTypes.DarkTheme) {
          document.body.classList.add("dark-theme");
          this.isDarkMode = true;
        } else {
          document.body.classList.remove("dark-theme");
          this.isDarkMode = false;
        }
      });

    // Notifications
    this.notifications$.pipe(filter((ns) => !!ns)).subscribe((ns) => {
      this.notifications = ns;
      this.unviewedNotifications = ns.filter((n) => !n.viewed).length;
    });

    // Undo keybinding
    merge(
      this.keybinding.create({ keys: "control.z" }), // PC
      this.keybinding.create({ keys: "meta.z" }), // Mac
    )
      .pipe(
        withLatestFrom(
          this.store.select(selectFocus),
          this.contextService.allContext$,
          this.store.select(selectWorkingCategoryState),
          this.store.select(selectDeltaModeState),
        ),
        map(([, focus, context, workingCategoryState, deltaState]) =>
          getContextKey({
            focus,
            context,
            categoryId: workingCategoryState.categoryID,
            deltaMode: !!deltaState.deltaCurrentExpression,
          }),
        ),
        filter((contextKey) => !!contextKey),
        filter((contextKey) => this.undoRedo.canUndo(contextKey)),
      )
      .subscribe((contextKey) => this.undoRedo.undo(contextKey));

    // Redo keybinding
    merge(
      this.keybinding.create({ keys: "control.y" }), // PC
      this.keybinding.create({ keys: "control.shift.z" }), // Alternate convention
      this.keybinding.create({ keys: "meta.y" }), // Mac
      this.keybinding.create({ keys: "meta.shift.z" }), // Mac alternate convention
    )
      .pipe(
        withLatestFrom(
          this.store.select(selectFocus),
          this.contextService.allContext$,
          this.store.select(selectWorkingCategoryState),
          this.store.select(selectDeltaModeState),
        ),
        map(([, focus, context, workingCategoryState, deltaState]) =>
          getContextKey({
            focus,
            context,
            categoryId: workingCategoryState.categoryID,
            deltaMode: !!deltaState.deltaCurrentExpression,
          }),
        ),
        filter((contextKey) => !!contextKey),
        filter((contextKey) => this.undoRedo.canRedo(contextKey)),
      )
      .subscribe((contextKey) => this.undoRedo.redo(contextKey));
  }

  initPadlock() {
    this.initStoreWithStateFromLocalStorage(padlockTokenFeatureKey, (state) => {
      if (state.token) {
        return new GetPadlockTokenSuccess(state.token);
      }
    });
  }

  initLog() {
    // Initialize the logging configuration service (listens for changes and updates logger)
    this.loggingConfigService.init();

    this.initStoreWithStateFromLocalStorage(
      loggingFeatureKey,
      (state) => new SetLoggingState(state),
    );
  }

  initNav() {
    this.initStoreWithStateFromLocalStorage(
      sideNavFeatureKey,
      (state) => new SetNavState(state),
    );
  }

  initStoreWithStateFromLocalStorage(
    featureKey: string,
    actionFactory: (state) => Action,
  ) {
    // Attempt to load from local storage
    try {
      const existingState = this.localStorageService.load(featureKey);
      if (existingState == null) {
        return;
      }

      const action = actionFactory(existingState);
      if (action) {
        this.store.dispatch(action);
      }
      this.logger.info(`APP: Using ${featureKey} state from local storage`);
    } catch (e) {
      this.logger.info(
        `APP: No state for ${featureKey} found in local storage; initial state will be used.`,
      );
    }
  }

  startupMessage() {
    this.logger.info(`##### Burrowing Owl Started #####`);
    this.logger.info(`Environment`, environment);
  }

  logout() {
    this.auth.logout();
  }

  activateWorkspace(workspace: Workspace) {
    this.store.dispatch(new SetActiveWorkspace(workspace, true));
  }

  newWorkspace() {
    this.workspaceService.newWorkspace();
  }

  onWorkspaceStateChange(workspace: Workspace) {
    this.workspace = workspace;
  }

  private groupWorkspaces() {
    const { mine, other } = partitionWorkspacesByOwnership(
      this.groupedWorkspaces,
      this.decodedToken?.sub ?? null,
    );
    this.myWorkspaces = mine;
    this.otherWorkspaces = other;
  }

  toggleDarkMode() {
    if (this.isDarkMode)
      this.themeService
        .setTheme("selected-theme", { theme: ThemeTypes.DarkTheme })
        .subscribe();
    else
      this.themeService
        .setTheme("selected-theme", { theme: ThemeTypes.LigthTheme })
        .subscribe();
  }

  markNotificationsViewed() {
    this.store.dispatch(markAllPersistentNotificationViewed());
  }
}
